/* DATA EXPLORER — the three panes on the right, as Power BI lays them out (Filters · Visualizations · Data), the filter
   dialog, the reports menu and the small dialogs (2026-10-22).

   The Data pane is the dataset's fields; ticking one puts it into the selected visual's natural box (a value into
   Values, anything else into the axis), with nothing selected it makes a new visual. The Visualizations pane picks the
   kind of the selected visual — or, with nothing selected, adds one — and shows its boxes. The Filters pane has
   Power BI's three levels: this visual, this page, all pages. */
import React, { useEffect, useRef, useState } from 'react';
import {
  ChartArea, ChartBar, ChartColumn, ChartColumnStacked, ChartLine, ChartNoAxesCombined, ChartPie, ChevronDown, ChevronRight,
  Database, Donut, Earth, Filter, FolderOpen, Grid3x3, GripVertical, LayoutGrid, Link2, ListFilter, Loader2, PanelRightClose,
  PanelRightOpen, Pencil, Plus, Save, Search, Share2, SquareSigma, Table2, Trash2, TrendingUp, X,
} from 'lucide-react';
import { CALCS, GRAINS, REL_PERIODS, REL_UNITS, SERIES, TYPE_ORDER, VISUAL_TYPES, WHOLE_PERIODS, keyLabel } from './explorerModel';

export const TYPE_ICON = {
  card: SquareSigma, kpi: TrendingUp, table: Table2, matrix: Grid3x3, column: ChartColumn, stacked: ChartColumnStacked,
  bar: ChartBar, line: ChartLine, area: ChartArea, combo: ChartNoAxesCombined, pie: ChartPie, donut: Donut, map: Earth, slicer: ListFilter,
};
const readDrag = (e) => { try { return JSON.parse(e.dataTransfer.getData('application/x-dx')); } catch { return null; } };

/* ================================================================ a pane's frame, folding to a strip */
function Pane({ id, title, icon: Icon, open, onToggle, children, className }) {
  if (!open) {
    return (
      <aside className={`dx-pane-col closed ${className || ''}`} aria-label={title}>
        <button type="button" className="dx-pane-strip" onClick={() => onToggle(id)} title={title} aria-label={title}>
          <PanelRightOpen size={15} /><span>{title}</span>
        </button>
      </aside>
    );
  }
  return (
    <aside className={`dx-pane-col ${className || ''}`} aria-label={title}>
      <div className="dx-pane-h">
        <span><Icon size={14} /> {title}</span>
        <button type="button" className="dx-vtool" onClick={() => onToggle(id)} title={title} aria-label={title}><PanelRightClose size={15} /></button>
      </div>
      <div className="dx-pane-b">{children}</div>
    </aside>
  );
}

/* ================================================================ Data */
export function DataPane({ ctx, sel, open, onToggle }) {
  const { t, fields, fieldLabel, actions } = ctx;
  const [q, setQ] = useState('');
  const [shut, setShut] = useState({});
  /* the known groups in their order, then any group a dataset brings that this list does not name (the operations datasets'
     status / people / checks, 31/10) — a field in an unlisted group was simply not drawn */
  const known = ['time', 'status', 'stock', 'product', 'people', 'org', 'geo', 'checks'];
  const groups = [...known, ...[...new Set((fields || []).map((f) => f.group))].filter((g) => g && g !== 'measures' && !known.includes(g)), 'measures'];
  const used = (f) => !!sel && [...sel.rows, ...sel.cols, ...sel.values].some((x) => x.field === f.key);
  return (
    <Pane id="data" title={t('xExplorer.dataPane')} icon={Database} open={open} onToggle={onToggle} className="dx-pane-data">
      <div className="dx-search sm">
        <Search size={13} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xExplorer.fieldSearch')} aria-label={t('xExplorer.fieldSearch')} />
      </div>
      {groups.map((g) => {
        const list = fields.filter((f) => f.group === g && (!q || fieldLabel(f.key).toLowerCase().includes(q.toLowerCase())));
        if (!list.length) return null;
        const closed = shut[g] && !q;
        return (
          <div key={g} className="dx-group">
            <button type="button" className="dx-group-h" aria-expanded={!closed} onClick={() => setShut((s) => ({ ...s, [g]: !s[g] }))}>
              {closed ? <ChevronRight size={13} /> : <ChevronDown size={13} />} {t(`xExplorer.group_${g}`)}
            </button>
            {!closed && list.map((f) => (
              <label key={f.key} className={`dx-field ${f.kind}`} draggable
                onDragStart={(e) => { e.dataTransfer.setData('application/x-dx', JSON.stringify({ field: f.key })); e.dataTransfer.effectAllowed = 'copyMove'; }}>
                <input type="checkbox" checked={used(f)} onChange={() => actions.toggleField(f.key)} aria-label={fieldLabel(f.key)} />
                <span className="dx-fname">{fieldLabel(f.key)}</span>
                <GripVertical size={12} className="dx-grip" aria-hidden="true" />
              </label>
            ))}
          </div>
        );
      })}
    </Pane>
  );
}

/* ================================================================ Visualizations */
export function VisualsPane({ ctx, sel, open, onToggle }) {
  const { t, actions, fields } = ctx;
  /* Build = what the visual shows; Format = how it reads (Power BI's two tabs). */
  const [tab, setTab] = useState('build');
  return (
    <Pane id="vis" title={t('xExplorer.visPane')} icon={ChartColumn} open={open} onToggle={onToggle} className="dx-pane-vis">
      <div className="dx-types" role="group" aria-label={t('xExplorer.visPane')}>
        {TYPE_ORDER.map((type) => {
          const Icon = TYPE_ICON[type];
          return (
            <button key={type} type="button" className={`dx-type${sel?.type === type ? ' on' : ''}`} aria-pressed={sel?.type === type}
              title={sel ? t(`xExplorer.vt_${type}`) : `${t('xExplorer.newVisual')}: ${t(`xExplorer.vt_${type}`)}`}
              aria-label={t(`xExplorer.vt_${type}`)}
              onClick={() => (sel ? actions.setType(sel.id, type) : actions.addVisual(type))}>
              <Icon size={18} />
            </button>
          );
        })}
      </div>
      {!sel && <p className="dx-hint">{t('xExplorer.visHint')}</p>}
      {sel && (
        <>
          <div className="dx-seltype">{t(`xExplorer.vt_${sel.type}`)}</div>
          {sel.type !== 'slicer' && (
            <div className="dx-vtabs" role="tablist">
              {['build', 'format'].map((k) => (
                <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''}
                  onClick={() => setTab(k)}>{t(`xExplorer.tab_${k}`)}</button>
              ))}
            </div>
          )}
          {(tab === 'build' || sel.type === 'slicer') ? (
            <>
              {VISUAL_TYPES[sel.type].wells.map(([well, label, max]) => (
                <WellBox key={well} ctx={ctx} v={sel} well={well} label={t(`xExplorer.w_${label}`)} max={max} />
              ))}
              {sel.type === 'slicer' && (
                <label className="dx-fmt-row">
                  <span>{t('xExplorer.fmtSlicerStyle')}</span>
                  <select value={sel.format?.slicerStyle || 'list'}
                    onChange={(e) => actions.patch(sel.id, { format: { ...(sel.format || {}), slicerStyle: e.target.value === 'list' ? undefined : e.target.value } })}>
                    {['list', 'dropdown'].map((x) => <option key={x} value={x}>{t(`xExplorer.fmtSlicer_${x}`)}</option>)}
                  </select>
                </label>
              )}
              <label className="dx-titlebox">
                <span>{t('xExplorer.vTitle')}</span>
                <input value={sel.title || ''} placeholder={ctx.autoTitle(sel)} maxLength={120}
                  onChange={(e) => actions.patch(sel.id, { title: e.target.value })} />
              </label>
            </>
          ) : <FormatBox ctx={ctx} v={sel} />}
        </>
      )}
      {fields.length === 0 && <Loader2 size={14} className="dx-spin" />}
    </Pane>
  );
}

function WellBox({ ctx, v, well, label, max }) {
  const { t, fieldLabel, fieldOf, actions } = ctx;
  const [over, setOver] = useState(false);
  /* A value renamed for this visual (Power BI's "Rename for this visual"): the index being typed into, and its text. */
  const [naming, setNaming] = useState(null);
  const items = v[well] || [];
  const isValues = well === 'values' || well === 'values2';
  const commitName = () => {
    if (!naming) return;
    const name = naming.text.trim().slice(0, 60);
    actions.setWellItem(v.id, well, naming.i, { label: name || undefined });
    setNaming(null);
  };
  /* A value may be shown as itself or compared with last year; a KPI compares by itself, a slicer has no values. */
  const offerCalc = (well === 'values' || well === 'values2') && v.type !== 'kpi';
  const drop = (index) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOver(false);
    const p = readDrag(e);
    if (p) actions.dropInWell(v.id, well, p, index);
  };
  return (
    <div className={`dx-well${over ? ' over' : ''}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)} onDrop={drop(items.length)}>
      <div className="dx-well-h">{label}{max > 1 ? <span className="dx-muted"> · {items.length}/{max}</span> : null}</div>
      {!items.length && <span className="dx-drop">{t('xExplorer.dropHere')}</span>}
      {items.map((it, i) => {
        const f = fieldOf(it.field);
        return (
          /* Two lines (2026-10-22, Max: "chỗ này thiết kế ko hợp lý" — name, rename, two selects and × in one line
             squeezed the name to nothing in a pane this narrow and pushed × out of it): the name with its own
             buttons, then the options, each select as wide as half the chip. */
          <div key={`${it.field}-${it.grain || ''}-${i}`} className="dx-chip" draggable={naming?.i !== i}
            onDragStart={(e) => e.dataTransfer.setData('application/x-dx', JSON.stringify({ field: it.field, from: { visual: v.id, well, index: i } }))}
            onDragOver={(e) => e.preventDefault()} onDrop={drop(i)}>
            <div className="dx-chip-top">
              {naming?.i === i ? (
                <input className="dx-chip-name" autoFocus value={naming.text} maxLength={60} aria-label={t('xExplorer.renameValue')}
                  placeholder={fieldLabel(it.field)}
                  onChange={(e) => setNaming({ i, text: e.target.value })} onBlur={commitName}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitName(); if (e.key === 'Escape') setNaming(null); }} />
              ) : (
                <span className={`dx-chip-l${it.label ? ' renamed' : ''}`}
                  title={it.label ? t('xExplorer.renamedFrom', { name: it.label, field: fieldLabel(it.field) }) : fieldLabel(it.field)}
                  onDoubleClick={isValues ? () => setNaming({ i, text: it.label || '' }) : undefined}>
                  {it.label || fieldLabel(it.field)}
                  {it.label && <small>{fieldLabel(it.field)}</small>}
                </span>
              )}
              {isValues && naming?.i !== i && (
                <button type="button" className="dx-x dx-chip-pen" aria-label={`${t('xExplorer.renameValue')}: ${it.label || fieldLabel(it.field)}`}
                  title={t('xExplorer.renameValue')} onClick={() => setNaming({ i, text: it.label || '' })}><Pencil size={12} /></button>
              )}
              <button type="button" className="dx-x" aria-label={`${t('xExplorer.remove')}: ${fieldLabel(it.field)}`} title={t('xExplorer.remove')}
                onClick={() => actions.removeWellItem(v.id, well, i)}><X size={12} /></button>
            </div>
            {(f?.kind === 'date' || (f?.kind === 'measure' && (f.aggs?.length > 1 || offerCalc))) && (
              <div className="dx-chip-opts">
                {f?.kind === 'date' && (
                  <select className="dx-mini" value={it.grain || ''} aria-label={t('xExplorer.grain')}
                    onChange={(e) => actions.setWellItem(v.id, well, i, { grain: e.target.value || undefined })}>
                    {v.type === 'slicer' && <option value="">{t('xExplorer.dateRange')}</option>}
                    {GRAINS.map((g) => <option key={g} value={g}>{t(`xExplorer.g_${g}`)}</option>)}
                  </select>
                )}
                {f?.kind === 'measure' && f.aggs?.length > 1 && (
                  <select className="dx-mini" value={it.agg} aria-label={t('xExplorer.aggregation')} title={t('xExplorer.aggregation')}
                    onChange={(e) => actions.setWellItem(v.id, well, i, { agg: e.target.value })}>
                    {f.aggs.map((a2) => <option key={a2} value={a2}>{t(`xExplorer.a_${a2}`)}</option>)}
                  </select>
                )}
                {f?.kind === 'measure' && offerCalc && (
                  <select className={`dx-mini dx-calc${it.calc ? ' on' : ''}`} value={it.calc || ''} aria-label={t('xExplorer.showAs')}
                    title={t('xExplorer.showAs')} onChange={(e) => actions.setWellItem(v.id, well, i, { calc: e.target.value || undefined })}>
                    <option value="">{t('xExplorer.calc_none')}</option>
                    {CALCS.map((c) => <option key={c} value={c}>{t(`xExplorer.calc_${c}`)}</option>)}
                  </select>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ================================================================ Format */
/* How the selected visual reads. Every setting is stored on the visual (v.format) and saved with the report; a
   setting a kind cannot use is not offered. */
function FormatBox({ ctx, v }) {
  const { t, actions, theme } = ctx;
  const fmt = v.format || {};
  const set = (patch) => actions.patch(v.id, { format: { ...fmt, ...patch } });
  const mode = theme === 'dark' ? 'dark' : 'light';
  const chart = VISUAL_TYPES[v.type]?.chart;
  const round = VISUAL_TYPES[v.type]?.round;
  const table = v.type === 'table' || v.type === 'matrix';
  /* Top N: where a list of things is drawn (bars, a table), not along time and not in a matrix's levels. */
  const topOk = ['column', 'stacked', 'bar', 'combo', 'table'].includes(v.type);
  /* One series takes any of the palette's hues; with a legend the order is the palette's own. */
  const oneSeries = chart && !v.cols.length && v.values.length + (v.values2 || []).length === 1;
  const intOf = (x) => (x === '' ? undefined : Number(x));
  return (
    <div className="dx-format">
      <label className="dx-fmt-row check">
        <input type="checkbox" checked={fmt.showTitle !== false} onChange={(e) => set({ showTitle: e.target.checked ? undefined : false })} />
        <span>{t('xExplorer.fmtShowTitle')}</span>
      </label>
      <div className="dx-fmt-h">{t('xExplorer.fmtNumbers')}</div>
      <label className="dx-fmt-row">
        <span>{t('xExplorer.fmtUnits')}</span>
        <select value={fmt.units || ''} onChange={(e) => set({ units: e.target.value || undefined })}>
          <option value="">{t('xExplorer.fmtUnits_default')}</option>
          {['auto', 'none', 'K', 'M', 'B'].map((u) => <option key={u} value={u}>{t(`xExplorer.fmtUnits_${u}`)}</option>)}
        </select>
      </label>
      <label className="dx-fmt-row">
        <span>{t('xExplorer.fmtDecimals')}</span>
        <select value={Number.isInteger(fmt.decimals) ? String(fmt.decimals) : ''} onChange={(e) => set({ decimals: intOf(e.target.value) })}>
          <option value="">{t('xExplorer.fmtAuto')}</option>
          {[0, 1, 2, 3].map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </label>
      {topOk && (
        <label className="dx-fmt-row">
          <span>{t('xExplorer.fmtTopN')}</span>
          <select value={fmt.topN ? String(fmt.topN) : ''} onChange={(e) => set({ topN: intOf(e.target.value) })}>
            <option value="">{t('xExplorer.fmtTopN_all')}</option>
            {[3, 5, 10, 15, 20, 25, 50].map((n) => <option key={n} value={n}>{t('xExplorer.fmtTopN_n', { n })}</option>)}
          </select>
        </label>
      )}
      {(chart || round) && (
        <>
          <div className="dx-fmt-h">{t('xExplorer.fmtChart')}</div>
          <label className="dx-fmt-row check">
            <input type="checkbox" checked={!!fmt.labels} onChange={(e) => set({ labels: e.target.checked || undefined })} />
            <span>{t('xExplorer.fmtLabels')}</span>
          </label>
          <label className="dx-fmt-row">
            <span>{t('xExplorer.fmtLegend')}</span>
            <select value={fmt.legend || ''} onChange={(e) => set({ legend: e.target.value || undefined })}>
              <option value="">{t('xExplorer.fmtAuto')}</option>
              {['top', 'bottom', 'right', 'none'].map((x) => <option key={x} value={x}>{t(`xExplorer.fmtLegend_${x}`)}</option>)}
            </select>
          </label>
          {oneSeries && (
            <div className="dx-fmt-row">
              <span>{t('xExplorer.fmtColour')}</span>
              <div className="dx-swatches" role="radiogroup" aria-label={t('xExplorer.fmtColour')}>
                {SERIES[mode].map((c, i) => (
                  <button key={c} type="button" role="radio" aria-checked={(fmt.color ?? 0) === i} className={(fmt.color ?? 0) === i ? 'on' : ''}
                    style={{ background: c }} aria-label={`${t('xExplorer.fmtColour')} ${i + 1}`} onClick={() => set({ color: i || undefined })} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {table && (
        <>
          <div className="dx-fmt-h">{t('xExplorer.fmtCond')}</div>
          <label className="dx-fmt-row">
            <span>{t('xExplorer.fmtCondKind')}</span>
            <select value={fmt.cond || 'none'} onChange={(e) => set({ cond: e.target.value === 'none' ? undefined : e.target.value })}>
              {['none', 'scale', 'bars'].map((x) => <option key={x} value={x}>{t(`xExplorer.fmtCond_${x}`)}</option>)}
            </select>
          </label>
          {fmt.cond && <p className="dx-hint">{t(`xExplorer.fmtCondHint_${fmt.cond}`)}</p>}
        </>
      )}
      <button type="button" className="dx-link dx-fmt-reset" disabled={!v.format || !Object.keys(v.format).length}
        onClick={() => actions.patch(v.id, { format: undefined })}>{t('xExplorer.fmtReset')}</button>
    </div>
  );
}

/* ================================================================ Filters */
export function FiltersPane({ ctx, sel, page, report, open, onToggle }) {
  const { t } = ctx;
  return (
    <Pane id="filters" title={t('xExplorer.filtersPane')} icon={Filter} open={open} onToggle={onToggle} className="dx-pane-filters">
      {sel && <FilterLevel ctx={ctx} level="visual" title={t('xExplorer.filtersVisual')} list={sel.filters} />}
      <FilterLevel ctx={ctx} level="page" title={t('xExplorer.filtersPage')} list={page.filters} />
      <FilterLevel ctx={ctx} level="report" title={t('xExplorer.filtersAll')} list={report.filters} />
    </Pane>
  );
}

function FilterLevel({ ctx, level, title, list }) {
  const { t, fields, fieldLabel, actions, filterSummary } = ctx;
  const [over, setOver] = useState(false);
  return (
    <div className={`dx-flevel${over ? ' over' : ''}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const p = readDrag(e); if (p?.field) actions.editFilter(level, null, p.field); }}>
      <div className="dx-flevel-h">{title}</div>
      {list.map((f, i) => (
        <div key={i} className={`dx-fcard${f.drill ? ' drill' : ''}`}>
          <button type="button" className="dx-fcard-l" onClick={() => actions.editFilter(level, i, f.field)} title={filterSummary(f)}>
            <b>{f.grain ? `${fieldLabel(f.field)} · ${t(`xExplorer.g_${f.grain}`)}` : fieldLabel(f.field)}</b>
            <span>{filterSummary(f, true)}</span>
          </button>
          <button type="button" className="dx-x" aria-label={`${t('xExplorer.remove')}: ${fieldLabel(f.field)}`} onClick={() => actions.removeFilter(level, i)}><X size={12} /></button>
        </div>
      ))}
      <select className="dx-addfilter" value="" aria-label={`${t('xExplorer.addFilter')} — ${title}`}
        onChange={(e) => { if (e.target.value) actions.editFilter(level, null, e.target.value); }}>
        <option value="">+ {t('xExplorer.addFilter')}</option>
        {fields.filter((f) => f.kind !== 'measure').map((f) => <option key={f.key} value={f.key}>{fieldLabel(f.key)}</option>)}
      </select>
    </div>
  );
}

/* ================================================================ the filter dialog */
export function FilterEditor({ t, datasetKey, field, fieldLabel, initial, onCancel, onApply, fetchValues }) {
  const isDateRange = field?.kind === 'date' && (!initial || initial.op === 'between' || initial.op === 'relative');
  const [from, setFrom] = useState(initial?.from || '');
  const [to, setTo] = useState(initial?.to || '');
  /* A date is filtered by a period that moves with the calendar (kept as its name) or by two fixed days. */
  const [dmode, setDmode] = useState(initial?.op === 'between' ? 'range' : 'relative');
  const [period, setPeriod] = useState(initial?.op === 'relative' ? initial.period : 'thisYear');
  const [lastN, setLastN] = useState(initial?.op === 'relative' && initial.period === 'lastN' ? String(initial.n) : '12');
  const [unit, setUnit] = useState(initial?.op === 'relative' && initial.period === 'lastN' ? initial.unit : 'month');
  const [whole, setWhole] = useState(initial?.op === 'relative' && !!initial.whole);
  const [mode, setMode] = useState(initial?.op && initial.op !== 'between' ? initial.op : 'in');
  const [picked, setPicked] = useState(() => new Set(initial?.values || []));
  const [text, setText] = useState(initial?.text || '');
  const [q, setQ] = useState('');
  const [list, setList] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);
  useEffect(() => {
    if (isDateRange || !field) return undefined;
    let live = true;
    const timer = setTimeout(() => {
      fetchValues(field.key, initial?.grain, q)
        .then((r) => { if (live) { setList(r); setErr(''); } })
        .catch((e) => { if (live) setErr(e.message); });
    }, q ? 250 : 0);
    return () => { live = false; clearTimeout(timer); };
  }, [q, field, datasetKey, isDateRange, initial, fetchValues]);
  if (!field) return null;
  /* A range that ends before it starts matches nothing, and every visual going blank does not say why. */
  const backwards = isDateRange && dmode === 'range' && from && to && from > to;
  const nBad = isDateRange && dmode === 'relative' && period === 'lastN' && !(Number(lastN) >= 1 && Number(lastN) <= 400 && Number.isInteger(Number(lastN)));
  const title = initial?.grain ? `${fieldLabel(field.key)} · ${t(`xExplorer.g_${initial.grain}`)}` : fieldLabel(field.key);
  const apply = () => {
    if (isDateRange && dmode === 'relative') {
      if (nBad) return;
      const w = whole && WHOLE_PERIODS.includes(period) ? { whole: true } : {};
      onApply(period === 'lastN' ? { field: field.key, op: 'relative', period, n: Number(lastN), unit, ...w } : { field: field.key, op: 'relative', period, ...w });
      return;
    }
    if (isDateRange) { onApply(from || to ? { field: field.key, op: 'between', from, to } : null); return; }
    if (mode === 'contains') { onApply(text.trim() ? { field: field.key, op: 'contains', text: text.trim() } : null); return; }
    const values = [...picked];
    onApply(values.length ? { field: field.key, op: mode, values, ...(initial?.grain ? { grain: initial.grain } : {}) } : null);
  };
  const toggle = (v) => setPicked((s) => { const n = new Set(s); if (n.has(v)) n.delete(v); else n.add(v); return n; });
  return (
    <div className="dx-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="dx-dialog" role="dialog" aria-modal="true" aria-label={title}>
        <div className="dx-dialog-h">
          <b>{title}</b>
          <button type="button" className="dx-x" onClick={onCancel} aria-label={t('xExplorer.fCancel')}><X size={14} /></button>
        </div>
        {isDateRange ? (
          <div className="dx-dates">
            <div className="dx-modes" role="radiogroup">
              {['relative', 'range'].map((m) => (
                <label key={m} className={dmode === m ? 'on' : ''}>
                  <input type="radio" name="dxdmode" checked={dmode === m} onChange={() => setDmode(m)} />
                  {t(`xExplorer.fMode_${m}`)}
                </label>
              ))}
            </div>
            {dmode === 'relative' ? (
              <>
                <div className="dx-rel" role="radiogroup" aria-label={t('xExplorer.fMode_relative')}>
                  {REL_PERIODS.map((p) => (
                    <button key={p} type="button" role="radio" aria-checked={period === p} className={`pa-btn${period === p ? ' on' : ''}`}
                      onClick={() => setPeriod(p)}>{t(`xExplorer.rel_${p}`)}</button>
                  ))}
                </div>
                <div className={`dx-lastn${period === 'lastN' ? ' on' : ''}`}>
                  <label className="dx-lastn-pick">
                    <input type="radio" name="dxlastn" checked={period === 'lastN'} onChange={() => setPeriod('lastN')} />
                    <span>{t('xExplorer.relLast')}</span>
                  </label>
                  <input type="number" min={1} max={400} value={lastN} aria-label={t('xExplorer.relLastN')}
                    onFocus={() => setPeriod('lastN')} onChange={(e) => { setPeriod('lastN'); setLastN(e.target.value); }} />
                  <select value={unit} aria-label={t('xExplorer.relUnit')} onChange={(e) => { setPeriod('lastN'); setUnit(e.target.value); }}>
                    {REL_UNITS.map((u) => <option key={u} value={u}>{t(`xExplorer.relUnit_${u}`)}</option>)}
                  </select>
                </div>
                {WHOLE_PERIODS.includes(period) && (
                  <label className="dx-fmt-row check dx-whole">
                    <input type="checkbox" checked={whole} onChange={(e) => setWhole(e.target.checked)} />
                    <span>{t(period === 'lastN' ? 'xExplorer.relWholeNOpt' : 'xExplorer.relWholeOpt')}</span>
                  </label>
                )}
                <p className="dx-hint">{t(period === 'lastN' ? (whole ? 'xExplorer.relHintNWhole' : 'xExplorer.relHintN') : (whole && WHOLE_PERIODS.includes(period) ? 'xExplorer.relHintWhole' : 'xExplorer.relHint'))}</p>
                {nBad && <div className="pa-banner err dx-range-err" role="alert">{t('xExplorer.relBadN')}</div>}
              </>
            ) : (
              <>
                <div className="dx-daterow">
                  <label><span>{t('xExplorer.fFrom')}</span><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
                  <label><span>{t('xExplorer.fTo')}</span><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
                </div>
                <button type="button" className="dx-link" onClick={() => { setFrom(''); setTo(''); }}>{t('xExplorer.fClearDates')}</button>
                {backwards && <div className="pa-banner err dx-range-err" role="alert">{t('xExplorer.fBackwards')}</div>}
              </>
            )}
          </div>
        ) : (
          <div className="dx-values">
            <div className="dx-modes" role="radiogroup">
              {['in', 'not_in', ...(field.kind === 'date' ? [] : ['contains'])].map((m) => (
                <label key={m} className={mode === m ? 'on' : ''}>
                  <input type="radio" name="dxmode" checked={mode === m} onChange={() => setMode(m)} />
                  {t(m === 'in' ? 'xExplorer.fInclude' : m === 'not_in' ? 'xExplorer.fExclude' : 'xExplorer.fContains')}
                </label>
              ))}
            </div>
            {mode === 'contains' ? (
              <input className="dx-text" autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder={t('xExplorer.containsText')} />
            ) : (
              <>
                <div className="dx-search">
                  <Search size={14} />
                  <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xExplorer.fSearch')} aria-label={t('xExplorer.fSearch')} />
                </div>
                <div className="dx-vtools-row">
                  <span>{t('xExplorer.fSelected', { n: picked.size })}</span>
                  <button type="button" className="dx-link" onClick={() => setPicked((s) => new Set([...s, ...(list?.values || []).map((x) => x.v)]))}>{t('xExplorer.fSelectShown')}</button>
                  <button type="button" className="dx-link" onClick={() => setPicked(new Set())}>{t('xExplorer.fClear')}</button>
                </div>
                <div className="dx-vlist">
                  {err && <div className="pa-banner err">{err}</div>}
                  {!list && !err && <div className="dx-muted"><Loader2 size={14} className="dx-spin" /></div>}
                  {list && !list.values.length && <div className="dx-muted">{t('xExplorer.fNone')}</div>}
                  {list?.values.map((x) => (
                    <label key={x.v} className="dx-vrow">
                      <input type="checkbox" checked={picked.has(x.v)} onChange={() => toggle(x.v)} />
                      <span className="dx-vname">{keyLabel({ grain: initial?.grain }, x.v)}</span>
                      <span className="dx-vn">{x.n.toLocaleString('en-US')}</span>
                    </label>
                  ))}
                  {list?.more && <div className="dx-muted">{t('xExplorer.fMore')}</div>}
                </div>
              </>
            )}
          </div>
        )}
        <div className="dx-dialog-f">
          <button type="button" className="pa-btn" onClick={onCancel}>{t('xExplorer.fCancel')}</button>
          <button type="button" className="pa-btn primary" onClick={apply} disabled={backwards || nBad}>{t('xExplorer.fApply')}</button>
        </div>
      </div>
    </div>
  );
}

/* ================================================================ the reports menu */
export function ReportsBar({ t, reports, current, name, dirty, editing, builder, onHome, onOpen, onNew, onSave, onSaveAs, onRename, onShare, onDelete, onLink, onEditing }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const key = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', key); };
  }, [open]);
  const mine = reports.filter((v) => v.mine);
  const shared = reports.filter((v) => !v.mine);
  return (
    <div className="dx-viewsbar" ref={ref}>
      <button type="button" className="pa-btn" onClick={onHome}><LayoutGrid size={15} /> {t('xExplorer.allReports')}</button>
      <div className="dx-current">
        <b>{name || t('xExplorer.untitled')}</b>
        {current?.shared && current?.mine && <span className="dx-badge">{t('xExplorer.sharedBadge')}</span>}
        {current && !current.mine && <span className="dx-badge">{t('xExplorer.sharedBy', { name: current.ownerName || '' })}{current.canEdit ? ` · ${t('xExplorer.roleEditor')}` : ''}</span>}
        {dirty && builder && <span className="dx-dirty">{t('xExplorer.unsaved')}</span>}
      </div>
      {builder && (
        <div className="dx-modeswitch" role="group" aria-label={t('xExplorer.editMode')}>
          <button type="button" className={editing ? 'on' : ''} aria-pressed={editing} onClick={() => onEditing(true)}>{t('xExplorer.editMode')}</button>
          <button type="button" className={!editing ? 'on' : ''} aria-pressed={!editing} onClick={() => onEditing(false)}>{t('xExplorer.readMode')}</button>
        </div>
      )}
      {current?.mine && builder && (
        <button type="button" className="pa-btn" onClick={onShare}><Share2 size={15} /> {t('xExplorer.share')}</button>
      )}
      <button type="button" className="pa-btn" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <FolderOpen size={15} /> {t('xExplorer.reports')} <ChevronDown size={13} />
      </button>
      {builder && (
        <button type="button" className="pa-btn primary" onClick={onSave} disabled={current?.canEdit && !dirty}>
          <Save size={15} /> {t('xExplorer.save')}
        </button>
      )}
      {open && (
        <div className="dx-menu" role="menu">
          {builder && (
            <>
              <button type="button" role="menuitem" className="dx-menu-i" onClick={() => { setOpen(false); onNew(); }}><Plus size={14} /> {t('xExplorer.newReport')}</button>
              <div className="dx-menu-sep" />
            </>
          )}
          {builder && <div className="dx-menu-h">{t('xExplorer.myReports')}</div>}
          {builder && !mine.length && <div className="dx-muted dx-menu-i">{t('xExplorer.noReports')}</div>}
          {mine.map((v) => (
            <button key={v.id} type="button" role="menuitem" className={`dx-menu-i${current?.id === v.id ? ' on' : ''}`} onClick={() => { setOpen(false); onOpen(v); }}>
              {v.name}{v.shared && <span className="dx-badge">{t('xExplorer.sharedBadge')}</span>}
            </button>
          ))}
          {shared.length > 0 && <div className="dx-menu-h">{t('xExplorer.sharedReports')}</div>}
          {shared.map((v) => (
            <button key={v.id} type="button" role="menuitem" className={`dx-menu-i${current?.id === v.id ? ' on' : ''}`} onClick={() => { setOpen(false); onOpen(v); }}>
              {v.name} <span className="dx-muted">{t('xExplorer.by', { name: v.ownerName || '' })}</span>
            </button>
          ))}
          <div className="dx-menu-sep" />
          {builder && <button type="button" role="menuitem" className="dx-menu-i" onClick={() => { setOpen(false); onSaveAs(); }}><Save size={14} /> {t('xExplorer.saveAs')}</button>}
          {current && (
            <>
              <button type="button" role="menuitem" className="dx-menu-i" onClick={() => { setOpen(false); onLink(); }}><Link2 size={14} /> {t('xExplorer.copyLink')}</button>
              {current.canEdit && builder && !current.mine && (
                <button type="button" role="menuitem" className="dx-menu-i" onClick={() => { setOpen(false); onRename(); }}>{t('xExplorer.rename')}</button>
              )}
              {current.mine && builder && (
                <>
                  <button type="button" role="menuitem" className="dx-menu-i" onClick={() => { setOpen(false); onRename(); }}>{t('xExplorer.rename')}</button>
                  <button type="button" role="menuitem" className="dx-menu-i" onClick={() => { setOpen(false); onShare(); }}>
                    <Share2 size={14} /> {t('xExplorer.share')}
                  </button>
                  <button type="button" role="menuitem" className="dx-menu-i danger" onClick={() => { setOpen(false); onDelete(); }}><Trash2 size={14} /> {t('xExplorer.delete')}</button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ================================================================ small dialogs */
const useEscape = (onCancel) => useEffect(() => {
  const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
  document.addEventListener('keydown', onKey);
  return () => document.removeEventListener('keydown', onKey);
}, [onCancel]);

export function NameDialog({ t, title, initial, okLabel, onCancel, onOk }) {
  const [v, setV] = useState(initial || '');
  useEscape(onCancel);
  return (
    <div className="dx-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <form className="dx-dialog narrow" role="dialog" aria-modal="true" aria-label={title}
        onSubmit={(e) => { e.preventDefault(); if (v.trim()) onOk(v.trim()); }}>
        <div className="dx-dialog-h"><b>{title}</b></div>
        <input className="dx-text" autoFocus value={v} maxLength={200} onChange={(e) => setV(e.target.value)} />
        <div className="dx-dialog-f">
          <button type="button" className="pa-btn" onClick={onCancel}>{t('xExplorer.fCancel')}</button>
          <button type="submit" className="pa-btn primary" disabled={!v.trim()}>{okLabel || t('xExplorer.save')}</button>
        </div>
      </form>
    </div>
  );
}

export function ConfirmDialog({ t, text, okLabel, onCancel, onOk, danger = true }) {
  useEscape(onCancel);
  return (
    <div className="dx-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="dx-dialog narrow" role="alertdialog" aria-modal="true">
        <p className="dx-confirm">{text}</p>
        <div className="dx-dialog-f">
          <button type="button" className="pa-btn" onClick={onCancel}>{t('xExplorer.fCancel')}</button>
          <button type="button" className={`pa-btn ${danger ? 'danger' : 'primary'}`} onClick={onOk}>
            {danger && <Trash2 size={14} />} {okLabel || t('xExplorer.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
